import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import SectionCard, { ViewAllLink } from "./SectionCard";
import { typeMeta } from "@/components/notifications/notificationMeta";

const TABS = [
  { key: "all", label: "All" },
  { key: "quotation_approval", label: "Approvals" },
  { key: "ai_preview_ready", label: "AI Previews" },
  { key: "followup_due", label: "Follow-ups" },
];

const COLLAPSED_COUNT = 4;

export default function AttentionFeed({ items }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("all");
  const [expanded, setExpanded] = useState(false);

  const filtered = tab === "all" ? items : items.filter((i) => i.type === tab);
  const visible = expanded ? filtered : filtered.slice(0, COLLAPSED_COUNT);
  const countFor = (key) => (key === "all" ? items.length : items.filter((i) => i.type === key).length);

  return (
    <SectionCard
      testId="attention-feed"
      title="Today's Attention"
      action={
        filtered.length > COLLAPSED_COUNT && (
          <ViewAllLink testId="attention-view-all" onClick={() => setExpanded((v) => !v)}>
            {expanded ? "Show less" : `View all (${filtered.length})`}
          </ViewAllLink>
        )
      }
    >
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 mb-3" role="tablist">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={active}
              data-testid={`attention-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={cn(
                "shrink-0 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm transition-colors",
                active
                  ? "bg-primary border-primary text-primary-foreground"
                  : "bg-card border-border text-foreground hover:bg-secondary",
              )}
            >
              {t.label}
              <span
                className={cn(
                  "rounded-md px-1.5 text-xs tabular-nums",
                  active ? "bg-primary-foreground/20" : "bg-secondary text-muted-foreground",
                )}
              >
                {countFor(t.key)}
              </span>
            </button>
          );
        })}
      </div>

      {visible.length === 0 && (
        <div className="py-10 text-center text-sm text-muted-foreground">
          You're all caught up — nothing needs your attention right now.
        </div>
      )}

      <ul className="divide-y divide-border">
        {visible.map((item, idx) => {
          const meta = typeMeta(item.type);
          const Icon = meta.icon;
          return (
            <li key={item.id} data-testid={`attention-item-${idx}`} className="flex items-center gap-3 sm:gap-4 py-3">
              <span className={cn("h-12 w-12 sm:h-14 sm:w-16 rounded-xl flex items-center justify-center shrink-0", meta.tile)}>
                <Icon className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <span className={cn("h-2 w-2 rounded-full shrink-0 hidden sm:block", meta.dot)} aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{item.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{item.subtitle}</p>
              </div>
              <span className="text-xs text-muted-foreground shrink-0 hidden md:block tabular-nums">
                {new Date(item.timestamp).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })}
              </span>
              <button
                onClick={() => navigate(item.actionUrl)}
                className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-border px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-foreground hover:border-primary hover:text-primary transition-colors"
              >
                {meta.cta} <ArrowRight className="h-3.5 w-3.5 hidden sm:block" />
              </button>
            </li>
          );
        })}
      </ul>
    </SectionCard>
  );
}
